import classNames from 'classnames';
import React, { SetStateAction, useState } from 'react'
import { connect, ConnectedProps } from 'react-redux';
import { Dispatch } from 'redux';
import { logActions, logSelectors } from 'redux/app/logs';
import { IStore } from 'redux/store';
import { IExpandedReportData, ITabReport } from 'redux/app/logs/@types';
import { IReportErrorDetails } from '../Reports';
import { rootRoutes } from 'routes';
import { allCNRColumns, cnrTabs, vesselSubTypeMap } from 'redux/app/vessel/onboard/constants';
import CargoReportRowExpanded from './tables/CargoReportRowExpanded';
import NoonReportRowExpanded from './tables/NoonReportRowExpanded';
import ArrivalDepartureReportRowExpanded from './tables/ArrivalDepartureReportRowExpanded';
import SOFReportRowExpanded from './tables/SOFReportRowExpanded';
import BunkerReportRowExpanded from './tables/BunkerReportRowExpanded';
import StoppageReportRowExpanded from './tables/StoppageReportRowExpanded';
import ToolsModal from '../ToolsModal';
import toast from "react-hot-toast";
import Axios from "axios";
import { Icon, Spinner } from '@blueprintjs/core';
import ModalPortal from 'components/app/_common/ModalPortal';
import { IStickyColumn } from 'redux/app/tool/@types';
import { getStickyColumnsOffsets } from 'redux/app/tool/utils';
import { getStoredColumns } from 'redux/app/vessel/onboard/utils';

type OwnProps = {
	pageNumber: string;
	vesselId: string;
	activeTab: string;
	setStatusModalOpen: React.Dispatch<SetStateAction<boolean>>;
	setErrorDetails: React.Dispatch<SetStateAction<IReportErrorDetails | null>>;
};

function NoonReportTable(props: OwnProps & PropsFromRedux) {
	const {
		report,
		reports,
		jobToLoadReports,
		vesselId,
		paginationOfReports,
		pageNumber,
		vesselSubType,
		activeTab,
		setStatusModalOpen,
		setErrorDetails,
		jobToLoadSelectedReport,
	} = props;

	const customColumns = getStoredColumns();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isToolsModalOpen, setIsToolsModalOpen] = useState<boolean>(false);
	const [remarksModal, setRemarksModal] = useState<string>("");

	function refreshReports() {
		props.loadExpandedReports(
			paginationOfReports.currentPage !== +pageNumber
				? +pageNumber
				: paginationOfReports.currentPage,
			true,
			vesselId || "",
			cnrTabs.find(tab => tab.id === activeTab)?.type || [],
		);
	}

	const handleToolAction = async (actionType: string, data: any) => {
		setIsLoading(true);
		const loadingToast = toast.loading(`Processing ${actionType.toLowerCase()} action...`);

		try {
			// Make the API call using axios
			const response = await Axios.post('/api/cnractions', {
				action: actionType,
				reportId: report._id,
				data: data
			});

			// if the response is successful, close the modal and refresh the page
			if (response.status === 200) {
				setIsToolsModalOpen(false);
				toast.success(`Successfully completed ${actionType.toLowerCase()} action`, {
					id: loadingToast,
					duration: 3000
				});
				refreshReports();
			} else {
				toast.error(`Failed to complete ${actionType.toLowerCase()} action`, {
					id: loadingToast,
					duration: 3000
				});
			}
		} catch (error: any) {
			console.error("Error handling tool action:", error);
			const errorMessage = error?.response?.data?.message || error?.message || "Unknown error occurred";
			toast.error(`Action failed: ${errorMessage}`, {
				id: loadingToast,
				duration: 5000
			});
		} finally {
			setIsLoading(false);
		}
	};

	// TODO: For print view
	// const setIsDragRef = useRef((value: boolean) => { });
	// const [reportOperation, setReportOperation] = useState<{
	// 	isModalOpen: boolean;
	// }>({
	// 	isModalOpen: false,
	// });
	// function selectReport(report: IExpandedReportData) {
	// 	if (report) {
	// 		setReportOperation((reportOperation) => {
	// 			return {
	// 				...reportOperation,
	// 				isModalOpen: true,
	// 			};
	// 		});
	// 		// props.loadReport(report?.data?._id);
	// 	}
	// }
	// function resetOperations() {
	// 	setReportOperation((reportOperation) => {
	// 		return {
	// 			...reportOperation,
	// 			isModalOpen: false,
	// 		};
	// 	});
	// }

	const selectedColumns = customColumns?.[activeTab] ?
		allCNRColumns?.[activeTab]?.filter(column => customColumns?.[activeTab]?.includes(column.label))
		: allCNRColumns?.[activeTab]?.filter(column => column?.isDefault);

	const isSignalStatusEnabled = customColumns?.[activeTab]?.includes("Signal Status");
	const isIMOSStatusEnabled = customColumns?.[activeTab]?.includes("IMOS Status");
	const isDocStatusEnabled = customColumns?.[activeTab]?.includes("Doc Status");

	const COL_WIDTH = { sm: 39, lg: 127 };
	const stickyColumns: IStickyColumn[] = [
		{ key: 'signalStatus', label: 'Signal Status', enabled: isSignalStatusEnabled, width: COL_WIDTH },
		{ key: 'imosStatus', label: 'IMOS Status', enabled: isIMOSStatusEnabled, width: COL_WIDTH },
		{ key: 'doc_status', label: 'Doc Status', enabled: isDocStatusEnabled, width: COL_WIDTH },
		{ key: 'actions', label: 'Actions', enabled: true, width: COL_WIDTH },
	];
	const stickyOffsets = getStickyColumnsOffsets(stickyColumns);

	return (
		<table
			className={classNames(
				"w-full relative mb-6 border bp3-interactive table-auto overflow-x-scroll",
				{ "animate-pulse": jobToLoadReports.isLoading }
			)}
		>

			{/* Table Headings */}
			<thead className="relative">
				<tr className="h-[52px] py-6">
					<th rowSpan={2} className="text-[9px] md:text-[12px] xl:text-sm text-gray-600 pl-2 min-w-10 lg:min-w-20 ">
						Sr No.
					</th>
					{selectedColumns?.map((column, index) => {
						if (column?.children && column.children.length > 0) {
							// Grouped columns
							if (column.subType && column.subType !== vesselSubTypeMap[vesselSubType]) return null;
							return (
								<th
									key={column.label}
									colSpan={column.children.length}
									className="border text-[9px] md:text-[12px] xl:text-sm text-center text-gray-600 min-w-32 "
								>
									{column.label}
								</th>
							);
						} else {
							return (
								<th
									key={column.label}
									rowSpan={2}
									className={classNames(
										"border text-[9px] md:text-[12px] xl:text-sm text-center text-gray-600 min-w-32",
										column.label === "Remarks" ? "w-104" : null

									)}
								>
									{column.label}
									{column.subLabel && (
										<div className="text-gray-500">{column.subLabel}</div>
									)}
								</th>
							);
						}
					})}
					{stickyColumns.filter(item => item.enabled).map(col => {
						const offset = stickyOffsets[col.key];
						return (
							<th
								key={col.key}
								rowSpan={2}
								className="border text-[9px] md:text-[12px] xl:text-sm text-gray-600 min-w-10 lg:min-w-32 sticky z-1 bg-gray-100"
								style={{ right: window.innerWidth < 1024 ? offset.sm : offset.lg }}
							>
								{col.label}
							</th>
						);
					})}
				</tr>

				<tr className="h-[52px]">
					{/* Children for grouped columns */}
					{selectedColumns?.map((column) => (
						column?.children?.map((child) => {
							if (column.subType && column.subType !== vesselSubTypeMap[vesselSubType]) return null;
							return (
								<th
									key={child.label}
									className="border text-[9px] md:text-[12px] xl:text-sm text-center text-gray-600 min-w-32 "
								>
									<div>
										<span className="text-gray-600 mr-1">{child.label}</span>
										{child.subLabel && (
											<span className="text-gray-500">{child.subLabel}</span>
										)}
									</div>
								</th>
							)
						})

					))}
				</tr>
			</thead>

			{/* Table Data */}
			<tbody
				className={classNames({
					"h-0 opacity-0 transition-all duration-100 max-h-0": jobToLoadReports.isLoading && reports?.length === 0,
					"h-auto opacity-100 transition-all duration-1000 delay-300 max-h-auto": !jobToLoadReports.isLoading,
				})}
			>
				{reports?.map((report, index) => {
					switch (activeTab) {
						case "noonReport":
							return (
								<NoonReportRowExpanded
									report={report}
									key={index}
									onClick={(report: IExpandedReportData) => {
										const cnrId = report?._id;
										if (vesselId)
											window.open(
												`${window.location.origin
												}${rootRoutes.children.report.children.report.url(
													cnrId
												)}`,
												"_blank"
											);
									}}
									indexNumber={
										(paginationOfReports.currentPage - 1) *
										paginationOfReports.itemsPerPage +
										(index + 1)
									}
									selectedColumns={selectedColumns}
									isSignalStatusEnabled={isSignalStatusEnabled}
									isIMOSStatusEnabled={isIMOSStatusEnabled}
									isDocStatusEnabled={isDocStatusEnabled}
									setStatusModalOpen={setStatusModalOpen}
									setErrorDetails={setErrorDetails}
									setIsToolsModalOpen={setIsToolsModalOpen}
									stickyOffsets={stickyOffsets}
									setRemarksModal={setRemarksModal}
								/>
							);
						case "arrivalsDepartures":
							return (
								<ArrivalDepartureReportRowExpanded
									report={report}
									key={index}
									onClick={(report: IExpandedReportData) => {
										const cnrId = report?._id;
										if (vesselId)
											window.open(
												`${window.location.origin
												}${rootRoutes.children.report.children.report.url(
													cnrId
												)}`,
												"_blank"
											);
									}}
									indexNumber={
										(paginationOfReports.currentPage - 1) *
										paginationOfReports.itemsPerPage +
										(index + 1)
									}
									selectedColumns={selectedColumns}
									isSignalStatusEnabled={isSignalStatusEnabled}
									isIMOSStatusEnabled={isIMOSStatusEnabled}
									isDocStatusEnabled={isDocStatusEnabled}
									setStatusModalOpen={setStatusModalOpen}
									setErrorDetails={setErrorDetails}
									setIsToolsModalOpen={setIsToolsModalOpen}
									stickyOffsets={stickyOffsets}
									setRemarksModal={setRemarksModal}
								/>
							);
						case "sofForms":
							return (
								<SOFReportRowExpanded
									report={report}
									key={index}
									onClick={(report: IExpandedReportData) => {
										const cnrId = report?._id;
										if (vesselId)
											window.open(
												`${window.location.origin
												}${rootRoutes.children.report.children.report.url(
													cnrId
												)}`,
												"_blank"
											);
									}}
									indexNumber={
										(paginationOfReports.currentPage - 1) *
										paginationOfReports.itemsPerPage +
										(index + 1)
									}
									selectedColumns={selectedColumns}
									isSignalStatusEnabled={isSignalStatusEnabled}
									isIMOSStatusEnabled={isIMOSStatusEnabled}
									isDocStatusEnabled={isDocStatusEnabled}
									setStatusModalOpen={setStatusModalOpen}
									setErrorDetails={setErrorDetails}
									setIsToolsModalOpen={setIsToolsModalOpen}
									stickyOffsets={stickyOffsets}
									setRemarksModal={setRemarksModal}
								/>
							);
						case "bunker":
							return (
								<BunkerReportRowExpanded
									report={report}
									key={index}
									onClick={(report: IExpandedReportData) => {
										const cnrId = report?._id;
										if (vesselId)
											window.open(
												`${window.location.origin
												}${rootRoutes.children.report.children.report.url(
													cnrId
												)}`,
												"_blank"
											);
									}}
									indexNumber={
										(paginationOfReports.currentPage - 1) *
										paginationOfReports.itemsPerPage +
										(index + 1)
									}
									selectedColumns={selectedColumns}
									isSignalStatusEnabled={isSignalStatusEnabled}
									isIMOSStatusEnabled={isIMOSStatusEnabled}
									isDocStatusEnabled={isDocStatusEnabled}
									setStatusModalOpen={setStatusModalOpen}
									setErrorDetails={setErrorDetails}
									setIsToolsModalOpen={setIsToolsModalOpen}
									stickyOffsets={stickyOffsets}
									setRemarksModal={setRemarksModal}
								/>
							);
						case "stoppages":
							return (
								<StoppageReportRowExpanded
									report={report}
									key={index}
									onClick={(report: IExpandedReportData) => {
										const cnrId = report?._id;
										if (vesselId)
											window.open(
												`${window.location.origin
												}${rootRoutes.children.report.children.report.url(
													cnrId
												)}`,
												"_blank"
											);
									}}
									indexNumber={
										(paginationOfReports.currentPage - 1) *
										paginationOfReports.itemsPerPage +
										(index + 1)
									}
									selectedColumns={selectedColumns}
									isSignalStatusEnabled={isSignalStatusEnabled}
									isIMOSStatusEnabled={isIMOSStatusEnabled}
									isDocStatusEnabled={isDocStatusEnabled}
									setStatusModalOpen={setStatusModalOpen}
									setErrorDetails={setErrorDetails}
									setIsToolsModalOpen={setIsToolsModalOpen}
									stickyOffsets={stickyOffsets}
									setRemarksModal={setRemarksModal}
								/>
							);
						case "cargo":
							return (
								<CargoReportRowExpanded
									report={report}
									key={index}
									onClick={(report: IExpandedReportData) => {
										const cnrId = report?._id;
										if (vesselId)
											window.open(
												`${window.location.origin
												}${rootRoutes.children.report.children.report.url(
													cnrId
												)}`,
												"_blank"
											);
									}}
									indexNumber={
										(paginationOfReports.currentPage - 1) *
										paginationOfReports.itemsPerPage +
										(index + 1)
									}
									selectedColumns={selectedColumns}
									vesselSubType={vesselSubType}
									isSignalStatusEnabled={isSignalStatusEnabled}
									isIMOSStatusEnabled={isIMOSStatusEnabled}
									isDocStatusEnabled={isDocStatusEnabled}
									setStatusModalOpen={setStatusModalOpen}
									setErrorDetails={setErrorDetails}
									setIsToolsModalOpen={setIsToolsModalOpen}
									stickyOffsets={stickyOffsets}
									setRemarksModal={setRemarksModal}
								/>
							);
						default:
							return null;
					}
				})}
			</tbody>

			{isToolsModalOpen ?
				!report || jobToLoadSelectedReport?.isLoading ?
					// Tools modal assumes it already has the report data, so we show a loading state until the report is loaded to avoid errors inside the modal
					<ModalPortal
						isActive={!report || jobToLoadSelectedReport?.isLoading}
						onClose={() => { }}
						width={"min(600px, 85%)"}
						isOverlayClickCloseable={!isLoading}
					>
						<div className="bg-white rounded-lg shadow-xl max-h-[90vh] flex flex-col">
							<div className="h-32 flex items-center justify-center">
								<Spinner />
							</div>
						</div>
					</ModalPortal>

					: <ToolsModal
						isOpen={isToolsModalOpen}
						onClose={() => setIsToolsModalOpen(false)}
						report={report}
						vesselName={report?.vesselName || ""}
						vesselImo={report?.vesselImo || ""}
						onAction={handleToolAction}
						isLoading={isLoading}
					/>
				: null
			}

			<ModalPortal
				isActive={!!remarksModal}
				onClose={() => setRemarksModal("")}
			>
				<div className='bg-white p-4 rounded-lg flex flex-col justify-between gap-4'>
					<div className='flex justify-between items-center border-b border-gray-300'>
						<div className='flex gap-2 items-center pb-1'>
							<Icon icon={"book"} size={20} color='#2d3748' className='border p-2 rounded-lg h-10 w-10 flex items-center justify-center' />
							<h3 className="text-lg font-medium text-gray-800">
								Remarks
							</h3>
						</div>
						<button
							className='text-gray-500 hover:text-gray-700 flex items-center'
							onClick={() => setRemarksModal("")}
						>
							<Icon icon="cross" />
						</button>
					</div>
					<div className='whitespace-pre-line text-[9px] md:text-xs lg:text-sm flex-1'>
						{remarksModal}
					</div>
					<div className='flex justify-center'>
						<button 
                    		className="px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-medium text-white bg-blue-600 rounded"
							onClick={() => setRemarksModal("")}
						>
							Close
						</button>
					</div>
				</div>
			</ModalPortal>

			{/* TODO: For print view */}
			{/* <ModalPortal
				isActive={reportOperation.isModalOpen}
				onClose={() => {
					resetOperations();
				}}
				isFullWidth
			>
				{reportOperation.isModalOpen ? (
					<TemplateRenderer
						setIsDragRef={setIsDragRef}
						onClose={function onClose() {
							props.clearSelectedReport();
							resetOperations();
						}}
					/>
				) : null}
			</ModalPortal> */}
		</table>
	)
}

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	const reportStore = store.app.logs;
	const report = logSelectors.getSelectedReport(reportStore);
	const reports = logSelectors.getExpandedReports(reportStore);
	const vesselStore = store.app.vesselStore;
	const vessel = vesselStore.vessels.byIds[ownProps.vesselId || ""] || {};
	const jobToLoadSelectedReport = logSelectors.jobToLoadSelectedReport(reportStore);

	return {
		report: report?.report as unknown as ITabReport,
		reports: reports,
		paginationOfReports: logSelectors.getReportsPagination(reportStore),
		jobToLoadReports: logSelectors.jobToLoadExpandedReports(reportStore),
		vesselSubType: vessel.vesselSubType || "",
		jobToLoadSelectedReport,
	};
}

function mapDispatchToProps(dispatch: Dispatch) {
	return {
		loadExpandedReports(
			pageNumber: number,
			showPageNumberInNotification: boolean = false,
			vesselId: string,
			reportTypes: string[],
		) {
			dispatch(
				logActions.commands.expandedReportsLoad({
					pageNumber,
					showPageNumberInNotification,
					vesselId,
					reportTypes,
				})
			);
		},
		// loadReport(reportId: string) {
		// 	dispatch(logActions.commands.singleReportLoad(reportId));
		// },
		// clearSelectedReport() {
		// 	dispatch(logActions.document.clearSelectedReport());
		// },
	};
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(NoonReportTable);